import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, Shield, Edit, Trash2 } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import userService from '../../../../services/userService';

export default function UserDetails() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { user, authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const result = await userService.getUserDetails(userId, authTokens.access);
      
      if (result.success) {
        setUserData(result.data);
      } else {
        showError(`Erreur: ${result.error}`);
        setTimeout(() => navigate('/dashboard/admin/users'), 2000);
      }
    } catch (error) {
      showError("Impossible de charger les détails de l'utilisateur");
      setTimeout(() => navigate('/dashboard/admin/users'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${userData.email} ?`)) {
      return;
    }

    try {
      // Note: You'll need to add deleteUser method to userService
      showSuccess('Fonctionnalité de suppression à implémenter');
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const canEditUser = () => {
    return user?.roles?.includes('Admin');
  };

  const canDeleteUser = () => {
    return user?.roles?.includes('Admin') && userData?.id !== user?.id;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
        Actif
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
        Inactif
      </span>
    );
  };

  const getRoleBadges = (roles) => {
    if (!roles || roles.length === 0) {
      return <span className="text-gray-500 italic">Aucun rôle assigné</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {roles.map((role, index) => (
          <span
            key={index}
            className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
          >
            {role}
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Utilisateur non trouvé.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard/admin/users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour aux utilisateurs
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <User className="h-8 w-8 text-teal-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {userData.first_name} {userData.last_name}
                </h1>
                <p className="text-gray-600">{userData.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(userData.is_active)}
              {canEditUser() && (
                <Link
                  to={`/dashboard/admin/users/edit/${userData.id}`}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Link>
              )}
              {/* {canDeleteUser() && (
                <button
                  onClick={handleDelete}
                  className="flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </button>
              )} */}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Informations personnelles
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Nom complet</p>
                    <p className="font-medium">{userData.first_name} {userData.last_name}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{userData.email}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium">{userData.contact || 'Non défini'}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Date de création</p>
                    <p className="font-medium">{formatDate(userData.date_joined)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Role and Organization Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Rôles et organisation
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <Shield className="h-4 w-4 text-gray-400 mr-3 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Rôles assignés</p>
                    <div className="mt-1">
                      {getRoleBadges(userData.roles)}
                    </div>
                  </div>
                </div>

                {userData.organisation && (
                  <div className="flex items-center">
                    <div className="h-4 w-4 text-gray-400 mr-3 flex items-center justify-center">
                      🏢
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Organisation</p>
                      <p className="font-medium">{userData.organisation.name}</p>
                      {userData.organisation.description && (
                        <p className="text-xs text-gray-400 mt-1">{userData.organisation.description}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <div className="h-4 w-4 text-gray-400 mr-3 flex items-center justify-center">
                    📊
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <div className="mt-1">
                      {getStatusBadge(userData.is_active)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {userData.last_login && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Informations de connexion
              </h3>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Dernière connexion</p>
                  <p className="font-medium">{formatDate(userData.last_login)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
